import { App, Leafer, Frame, type IUI, Image as LeaferImage, Text as LeaferText } from 'leafer-editor'
import { GuideLines } from 'leafer-x-guide-line'
import { Ruler } from 'leafer-x-ruler'
import type { SkuItem, PrintImage } from '@podesigner/core'
import { generateId } from '@podesigner/core'
import { ref, type Ref } from 'vue'

export interface EditorConfig {
  container: HTMLElement
  width?: number
  height?: number
  enableGuideLines?: boolean
  enableRuler?: boolean
}

export interface EditorOptions {
  strokeColor?: string
  strokeWidth?: number
  cornerRadius?: number
}

export class BaseEditor {
  public app!: App
  public elementParent!: Leafer | IUI
  public currentElement: Ref<IUI | null> = ref<IUI | null>(null)
  public printImages: Ref<PrintImage[]> = ref([])
  public initialized = ref(false)
  public currentFrame?: Frame
  public guideLines?: GuideLines
  public ruler?: Ruler
  public zoom = ref(0.25)
  
  // SKU相关
  public skus = new Map<string, SkuItem>()
  public currentSkuKey: string = ''
  public currentSkuFaceKey: string = ''
  public currentFaceName: string = ''
  public commonSkuChildren: Map<string, IUI[]> = new Map<string, IUI[]>()

  private config: EditorConfig
  private options: EditorOptions

  constructor(config: EditorConfig, options: EditorOptions = {}) {
    this.config = config
    this.options = {
      strokeColor: '#F68066',
      strokeWidth: 1,
      cornerRadius: 0,
      ...options
    }
  }

  /**
   * 初始化编辑器
   */
  public init(): void {
    this.createApp()
    this.setupGuideLines()
    this.setupRuler()
    this.setupWorkspace()
    this.initialized.value = true
  }

  /**
   * 创建主应用
   */
  private createApp(): void {
    this.app = new App({
      view: this.config.container,
      width: this.config.width || 1200,
      height: this.config.height || 1200,
      tree: {
        type: 'editor'
      },
      editor: {
        point: { cornerRadius: this.options.cornerRadius },
        middlePoint: {},
        rotatePoint: { width: 16, height: 16 },
        rect: { dashPattern: [3, 2] },
        buttonsDirection: 'top',
        stroke: this.options.strokeColor,
        strokeWidth: this.options.strokeWidth,
        buttonsFixed: true,
      },
      wheel: {
        disabled: true,
        preventDefault: false,
      },
    })

    this.app.tree.fill = 'transparent'
  }

  /**
   * 设置吸附线
   */
  private setupGuideLines(): void {
    if (this.config.enableGuideLines !== false) {
      this.guideLines = new GuideLines(this.app, {
        enabled: true,
        alignLineMargin: 10,
        alignLineWidth: 1,
        signSize: 3,
        lineStyle: { type: 'dashed' },
        alignLineColor: this.options.strokeColor || '#F68066'
      })
    }
  }

  /**
   * 设置标尺
   */
  private setupRuler(): void {
    if (this.config.enableRuler !== false) {
      this.ruler = new Ruler(this.app, {
        enabled: true,
        theme: "light",
      })
    }
  }

  /**
   * 设置工作区
   */
  private setupWorkspace(): void {
    const userImageGroup = new Frame({
      lockRatio: true,
      zIndex: 1,
      resizeChildren: true,
      name: 'workspace',
      fill: {
        type: 'solid',
        color: '#ffffff01',
      }
    })

    this.elementParent = userImageGroup
    this.currentFrame = userImageGroup
    this.app.tree.add(userImageGroup)
  }

  /**
   * 添加图片元素
   */
  public addImage(url: string, options?: { x?: number, y?: number, width?: number, height?: number }): LeaferImage {
    const image = new LeaferImage({
      url,
      x: options?.x || 0,
      y: options?.y || 0,
      width: options?.width,
      height: options?.height,
    })

    this.elementParent.add(image)
    return image
  }

  /**
   * 添加文本元素
   */
  public addText(text: string, options?: { x?: number, y?: number, fontSize?: number, fill?: string }): LeaferText {
    const textElement = new LeaferText({
      text,
      x: options?.x || 0,
      y: options?.y || 0,
      fontSize: options?.fontSize || 16,
      fill: options?.fill || '#000000',
    })

    this.elementParent.add(textElement)
    return textElement
  }

  /**
   * 删除元素
   */
  public removeElement(element: IUI): void {
    // 从打印图片记录中移除
    this.printImages.value = this.printImages.value.filter(img => img.id !== element.id)
    element.remove()
  }

  /**
   * 清空画布
   */
  public clear(): void {
    if (this.elementParent && 'removeAll' in this.elementParent) {
      ;(this.elementParent as any).removeAll()
    }
    this.printImages.value = []
  }

  /**
   * 获取所有元素
   */
  public getAllElements(): IUI[] {
    return this.elementParent.children || []
  }

  /**
   * 设置缩放
   */
  public setZoom(zoom: number): void {
    this.zoom.value = zoom
    this.app.tree.scale = zoom
  }

  /**
   * 获取当前缩放
   */
  public getZoom(): number {
    return this.zoom.value
  }

  /**
   * 销毁编辑器
   */
  public destroy(): void {
    this.app?.destroy()
    this.initialized.value = false
  }
} 